import {
  Autocomplete,
  IconButton,
  InputAdornment,
  ListItemText,
  Box,
  AutocompleteChangeDetails,
  useMediaQuery,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {
  Dispatch,
  forwardRef,
  SetStateAction,
  SyntheticEvent,
  useState,
} from 'react'
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom'
import theme from 'lib/theme'
import { useTranslation } from 'react-i18next'
import { DateRangePicker } from 'components/DateRangePicker/DateRangePicker'
import { useSearchMenuOptions } from 'hooks/useSearchMenuOptions'
import {
  SearchField,
  OrderSearchIcon,
  SearchMenuItem,
} from './OrderSearchBar.styled'
import { OrderSearchParams } from './OrderSearchParams'

type OrderSearchBarProps = {
  error: boolean
  isLoading: boolean
  places: { label: string; value: string }[]
  placeholderText: string
  ariaLabel: string
  input: string
  setInput: Dispatch<SetStateAction<string>>
  dateRange: string[]
  queries: { [key: string]: string } | null
  placeInfo: { label: string; value: string }[]
}

const ListBoxComponent = forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  return (
    <ul
      ref={ref}
      {...props}
      role='listbox'
      style={{
        padding: 0,
        maxHeight: 'none',
      }}
    />
  )
})

export const OrderSearchBar = ({
  error,
  isLoading,
  places,
  placeholderText,
  ariaLabel,
  input,
  setInput,
  dateRange,
  queries,
  placeInfo,
}: OrderSearchBarProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as { placeName: string }
  const [focus, setFocus] = useState(false)
  const [hover, setHover] = useState(false)
  const [iconHover, setIconHover] = useState<string>('')
  const [errorMessage] = useState<string>('')
  const [isOpen, setIsOpen] = useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { menuOptions } = useSearchMenuOptions(input, places)

  const menuDisplayValue = (option: {
    key: string
    label: string
    status?: number
    placeName?: string
    placeId?: string
  }) => {
    if (option.key === 'place') {
      return option.placeName
    }
    if (option.key === 'status') {
      return t(option.label)
    }
    return input
  }

  const clearSearch = () => {
    setInput('')
    navigate('/order')
  }
  const handleSearch = (
    options: AutocompleteChangeDetails<{
      key: string
      label: string
      status?: number
      placeName?: string
      placeId?: string
    }>
  ) => {
    const { key } = options.option
    const newQuery = () => {
      if (key === 'place') {
        return { place_id: options.option.placeId }
      }
      if (key === 'status') {
        return { status: options.option.status }
      }
      return { [key]: input }
    }
    const formattedParams = createSearchParams({
      start_delivery_date: dateRange[0],
      end_delivery_date: dateRange[1],
      page: '1',
      ...queries,
      ...(newQuery() as { [key: string]: string }),
    })
    navigate(`/order?${formattedParams}`, {
      state: locationState,
    })
  }
  return (
    <Box
      sx={{
        height: 'auto',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        flexFlow: 'row wrap',
        justifyContent: 'space-between',
      }}>
      <Autocomplete
        disableClearable
        forcePopupIcon={false}
        options={menuOptions}
        id='order'
        open={isOpen}
        role='search'
        value={{ key: '', label: input }}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        onMouseOver={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        filterOptions={(options) => options}
        sx={{
          marginBottom: theme.spacing(1),
        }}
        onInputChange={(
          event: SyntheticEvent<Element, Event>,
          value: string,
          reason: string
        ) => {
          if (reason === 'reset') {
            setInput('')
            setIsOpen(false)
          }
          if (reason === 'input') {
            setInput(value)
            if (value.length > 2) {
              setIsOpen(true)
            } else {
              setIsOpen(false)
            }
          }
        }}
        onChange={(e, value, reason, details) => {
          if (reason === 'selectOption') {
            if (details) {
              handleSearch(details)
            }
          }
        }}
        loading={isLoading}
        disablePortal
        freeSolo
        ListboxComponent={ListBoxComponent}
        renderOption={(props, option) => (
          <SearchMenuItem
            {...props}
            width={isMobile ? '70vw' : ' 100%'}
            key={option.key + option.label + option.placeId}
            aria-hidden
            onMouseOver={() => setIconHover(option.key)}
            onMouseLeave={() => setIconHover('')}
            iconHover={iconHover === option.key}
            value={input}
            aria-label={ariaLabel}>
            <ListItemText>
              <b>
                {' '}
                {option.key.includes('status')
                  ? t('status')
                  : t(option.label)}{' '}
              </b>
              {menuDisplayValue(option)}
            </ListItemText>
            <OrderSearchIcon aria-hidden hover={iconHover === option.key} />
          </SearchMenuItem>
        )}
        noOptionsText={t('noOptionsAutoComplete')}
        renderInput={(params) => (
          <SearchField
            {...params}
            queries={queries}
            backgroundColor={
              Object.keys(queries ?? {})?.length > 0
                ? theme.palette.commonColors.white
                : theme.palette.secondary.main
            }
            error={error}
            helperText={
              errorMessage ||
              ((!input || (input && input.length < 3)) && focus
                ? t('enterCharacters')
                : ' ')
            }
            placeholder={placeholderText}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <OrderSearchParams
                  hover={hover}
                  queries={queries}
                  dateRange={dateRange}
                  placeInfo={placeInfo}
                />
              ),
              ...((Object.keys(queries ?? {})?.length > 0 || focus) && {
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onMouseDown={clearSearch}
                      aria-label={t('clearSearch')}
                      sx={{
                        '&:hover': {
                          cursor: 'pointer',
                          background: 'transparent',
                          color: theme.palette.action.hover,
                        },
                      }}>
                      <CloseIcon
                        sx={{
                          '&:hover': {
                            background: 'transparent',
                          },
                        }}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }),
            }}
          />
        )}
      />
      <DateRangePicker dateRange={dateRange} queries={queries} />
    </Box>
  )
}
